import type { BootstrapModal } from "./types/bootstrap.js";

// TypeScript interfaces
interface ModalField {
  id: string;
  type: 'text' | 'email' | 'password' | 'textarea' | 'select' | 'checkbox';
  label: string;
  name?: string;
  placeholder?: string;
  value?: string | boolean;
  required?: boolean;
  disabled?: boolean;
  class?: string;
  help?: string;
  rows?: number; // for textarea
  options?: Array<{ value: string; text: string }>; // for select
  checked?: boolean; // for checkbox
}

interface ModalButton {
  text: string;
  action?: string;
  class?: string;
  dismiss?: boolean;
  disabled?: boolean;
  icon?: string;
}

interface ModalOptions {
  id?: string;
  title?: string;
  message?: string;
  html?: string;
  icon?: string;
  iconClass?: string;
  iconSize?: string;
  size?: 'modal-sm' | 'modal-lg' | 'modal-xl';
  centered?: boolean;
  scrollable?: boolean;
  backdrop?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  showCloseButton?: boolean;
  headerClass?: string;
  bodyClass?: string;
  footerClass?: string;
  messageClass?: string;
  fields?: ModalField[];
  buttons?: ModalButton[];
  formId?: string;
}

interface ModalResult {
  action: string | null;
  formData?: Record<string, any>;
  dismissed: boolean;
}

// Bootstrap types now imported from centralized bootstrap.d.ts
const modalTemplate = `
<div class="modal fade" id="{{ id | default('defaultModal') }}" tabindex="-1" aria-labelledby="{{ id | default('defaultModal') }}Label" aria-hidden="true" {{ 'data-bs-backdrop="static"' if backdrop === false else '' }}>
  <div class="modal-dialog {{ size | default('') }} {{ centered | default(false) and 'modal-dialog-centered' or '' }} {{ scrollable | default(false) and 'modal-dialog-scrollable' or '' }}">
    <div class="modal-content">
      {% if title or showHeader !== false %}
      <div class="modal-header {{ headerClass | default('') }}">
        {% if title %}
        <h1 class="modal-title fs-5" id="{{ id | default('defaultModal') }}Label">{{ title }}</h1>
        {% endif %}
        {% if showCloseButton !== false %}
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        {% endif %}
      </div>
      {% endif %}
      
      <div class="modal-body {{ bodyClass | default('') }}">
        {% if icon %}
        <div class="text-center mb-3">
          <i class="{{ icon }} {{ iconClass | default('') }}" style="font-size: {{ iconSize | default('3rem') }};"></i>
        </div>
        {% endif %}
        
        {% if message %}
        <div class="{{ messageClass | default('') }}">{{ message | safe }}</div>
        {% endif %}
        
        {% if html %}
        <div>{{ html | safe }}</div>
        {% endif %}
        
        {% if fields %}
        <form id="{{ formId | default(id + 'Form') }}">
          {% for field in fields %}
          <div class="mb-3">
            {% if field.type === 'text' or field.type === 'email' or field.type === 'password' %}
            <label for="{{ field.id }}" class="form-label">{{ field.label }}</label>
            <input type="{{ field.type }}" class="form-control {{ field.class | default('') }}" id="{{ field.id }}" name="{{ field.name | default(field.id) }}" 
                   placeholder="{{ field.placeholder | default('') }}" value="{{ field.value | default('') }}" 
                   {{ field.required and 'required' or '' }} {{ field.disabled and 'disabled' or '' }}>
            {% elif field.type === 'textarea' %}
            <label for="{{ field.id }}" class="form-label">{{ field.label }}</label>
            <textarea class="form-control {{ field.class | default('') }}" id="{{ field.id }}" name="{{ field.name | default(field.id) }}" 
                      rows="{{ field.rows | default(3) }}" placeholder="{{ field.placeholder | default('') }}" 
                      {{ field.required and 'required' or '' }} {{ field.disabled and 'disabled' or '' }}>{{ field.value | default('') }}</textarea>
            {% elif field.type === 'select' %}
            <label for="{{ field.id }}" class="form-label">{{ field.label }}</label>
            <select class="form-select {{ field.class | default('') }}" id="{{ field.id }}" name="{{ field.name | default(field.id) }}" 
                    {{ field.required and 'required' or '' }} {{ field.disabled and 'disabled' or '' }}>
              {% if field.placeholder %}
              <option value="">{{ field.placeholder }}</option>
              {% endif %}
              {% for option in field.options %}
              <option value="{{ option.value }}" {{ option.value === field.value and 'selected' or '' }}>{{ option.text }}</option>
              {% endfor %}
            </select>
            {% elif field.type === 'checkbox' %}
            <div class="form-check">
              <input class="form-check-input {{ field.class | default('') }}" type="checkbox" id="{{ field.id }}" name="{{ field.name | default(field.id) }}" 
                     value="{{ field.value | default('1') }}" {{ field.checked and 'checked' or '' }} {{ field.disabled and 'disabled' or '' }}>
              <label class="form-check-label" for="{{ field.id }}">{{ field.label }}</label>
            </div>
            {% endif %}
            {% if field.help %}
            <div class="form-text">{{ field.help }}</div>
            {% endif %}
          </div>
          {% endfor %}
        </form>
        {% endif %}
      </div>
      
      {% if buttons or showFooter !== false %}
      <div class="modal-footer {{ footerClass | default('') }}">
        {% if buttons %}
          {% for button in buttons %}
          <button type="button" class="btn {{ button.class | default('btn-secondary') }}" 
                  {{ button.dismiss and 'data-bs-dismiss="modal"' or '' }}
                  data-action="{{ button.action | default('') }}"
                  {{ button.disabled and 'disabled' or '' }}>
            {% if button.icon %}<i class="{{ button.icon }}"></i> {% endif %}{{ button.text }}
          </button>
          {% endfor %}
        {% else %}
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Chiudi</button>
        {% endif %}
      </div>
      {% endif %}
    </div>
  </div>
</div>`;

/**
 * Renders modal HTML without Nunjucks dependency
 * @param options - Modal configuration options
 * @returns Rendered HTML string
 */
const templateRender = (options: ModalOptions): string => {
  const {
    id = 'defaultModal',
    title,
    message,
    html,
    icon,
    iconClass = '',
    iconSize = '3rem',
    size = '',
    centered = false,
    scrollable = false,
    backdrop = true,
    showHeader,
    showFooter,
    showCloseButton,
    headerClass = '',
    bodyClass = '',
    footerClass = '',
    messageClass = '',
    fields,
    buttons,
    formId
  } = options;

  // CSS class helper
  const cssClass = (condition: boolean | undefined, className: string) => 
    condition ? ` ${className}` : '';

  // Simple form - only single text input for showPrompt
  const renderSimpleForm = (): string => {
    if (!fields || fields.length === 0) return '';
    const field = fields[0]; // Only first field matters
    
    return `
      <form id="${formId || id + 'Form'}">
        <div class="mb-3">
          <label for="${field.id}" class="form-label">${field.label}</label>
          <input type="text" 
                 class="form-control" 
                 id="${field.id}"
                 name="${field.name || field.id}"
                 placeholder="${field.placeholder || ''}" 
                 value="${field.value || ''}"
                 ${field.required ? 'required' : ''}>
        </div>
      </form>`;
  };

  // Simple buttons
  const renderButtons = (): string => {
    if (!buttons) {
      return '<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Chiudi</button>';
    }
    
    return buttons.map(button => 
      `<button type="button" class="btn ${button.class || 'btn-secondary'}"
               ${button.dismiss ? 'data-bs-dismiss="modal"' : ''}
               data-action="${button.action || ''}"
               ${button.disabled ? 'disabled' : ''}>
         ${button.icon ? `<i class="${button.icon}"></i> ` : ''}${button.text}
       </button>`
    ).join('');
  };

  // Main template structure
  return `
<div class="modal fade" id="${id}" tabindex="-1" aria-labelledby="${id}Label" aria-hidden="true"${backdrop === false ? ' data-bs-backdrop="static"' : ''}>
  <div class="modal-dialog${cssClass(!!size, size)}${cssClass(centered, 'modal-dialog-centered')}${cssClass(scrollable, 'modal-dialog-scrollable')}">
    <div class="modal-content">
      ${(title || showHeader !== false) ? `
      <div class="modal-header ${headerClass}">
        ${title ? `<h1 class="modal-title fs-5" id="${id}Label">${title}</h1>` : ''}
        ${showCloseButton !== false ? '<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>' : ''}
      </div>` : ''}
      
      <div class="modal-body ${bodyClass}">
        ${icon ? `
        <div class="text-center mb-3">
          <i class="${icon} ${iconClass}" style="font-size: ${iconSize};"></i>
        </div>` : ''}
        
        ${message ? `<div class="${messageClass}">${message}</div>` : ''}
        ${html ? `<div>${html}</div>` : ''}
        
        ${renderSimpleForm()}
      </div>
      
      ${(buttons || showFooter !== false) ? `
      <div class="modal-footer ${footerClass}">
        ${renderButtons()}
      </div>` : ''}
    </div>
  </div>
</div>`;
};

/**
 * Internal function to show a modal with the given options
 * @param options - Modal configuration options
 * @returns Promise that resolves with the modal result
 */
const _showModalInternal = async (options: ModalOptions): Promise<ModalResult> => {
  return new Promise((resolve) => {
    // Generate unique ID if not provided
    const modalId = options.id || `modal_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    const modalOptions = { ...options, id: modalId };

    // Render template and create modal element
    // const modalHtml = templateRenderString(modalTemplate, modalOptions);
    const modalHtml = templateRender(modalOptions);
    const modalElement = document.createElement('div');
    modalElement.innerHTML = modalHtml;
    const modalElementActual = modalElement.firstElementChild as HTMLElement;
    
    // Add modal to DOM
    document.body.appendChild(modalElementActual);

    // Initialize Bootstrap modal
    const bsModal: BootstrapModal = new window.bootstrap.Modal(modalElementActual, {
      backdrop: options.backdrop !== false ? true : 'static',
      keyboard: options.backdrop !== false
    });

    // Helper function to collect form data
    const getFormData = (): Record<string, any> => {
      const formData: Record<string, any> = {};
      if (options.fields) {
        options.fields.forEach(field => {
          const element = modalElementActual.querySelector(`[name="${field.name || field.id}"]`) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
          if (element) {
            if (field.type === 'checkbox') {
              formData[field.name || field.id] = (element as HTMLInputElement).checked;
            } else {
              formData[field.name || field.id] = element.value;
            }
          }
        });
      }
      return formData;
    };

    // Helper function to resolve and cleanup
    const resolveAndCleanup = (result: ModalResult) => {
      // Set inert to prevent focus issues before hiding
      modalElementActual.setAttribute('inert', '');
      
      // Remove modal from DOM after hide animation
      modalElementActual.addEventListener('hidden.bs.modal', () => {
        modalElementActual.remove();
      }, { once: true });
      
      bsModal.hide();
      resolve(result);
    };

    // Handle button clicks
    modalElementActual.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const button = target.closest('[data-action]') as HTMLElement;
      
      if (button) {
        const action = button.getAttribute('data-action') || '';
        const formData = getFormData();
        
        resolveAndCleanup({
          action,
          formData,
          dismissed: false
        });
      }
    });

    // Handle modal dismissal (X button, backdrop click, ESC key)
    modalElementActual.addEventListener('hide.bs.modal', () => {
      // Set inert to prevent focus issues before hiding
      modalElementActual.setAttribute('inert', '');
    });

    modalElementActual.addEventListener('hidden.bs.modal', () => {
      const formData = getFormData();
      resolve({
        action: null,
        formData,
        dismissed: true
      });
    }, { once: true });

    // Handle form submission
    const form = modalElementActual.querySelector('form');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const formData = getFormData();
        
        resolveAndCleanup({
          action: 'submit',
          formData,
          dismissed: false
        });
      });
    }

    // Show the modal
    bsModal.show();
    
    // Remove inert when modal is fully shown to allow interaction
    modalElementActual.addEventListener('shown.bs.modal', () => {
      modalElementActual.removeAttribute('inert');
    }, { once: true });
  });
}
/**
 * Shows an error modal with a red icon and close button
 * @param message - Error message to display
 * @param options - Additional modal options
 * @returns Promise that resolves when modal is closed
 */
export const showError = async (message: string, options: Partial<ModalOptions> = {}): Promise<ModalResult> => {
  return _showModalInternal({
    title: 'Errore',
    message,
    icon: 'fas fa-exclamation-triangle',
    iconClass: 'text-danger',
    headerClass: 'border-danger',
    buttons: [
      { text: 'Chiudi', action: 'close', class: 'btn-danger', dismiss: true }
    ],
    ...options
  });
};

/**
 * Shows a confirmation modal with yes/no buttons
 * @param message - Confirmation message to display
 * @param options - Additional modal options
 * @returns Promise that resolves with boolean (true if confirmed, false if cancelled/dismissed)
 */
export const showConfirm = async (message: string, options: Partial<ModalOptions> = {}): Promise<boolean> => {
  const result = await _showModalInternal({
    title: 'Conferma',
    message,
    icon: 'fas fa-question-circle',
    iconClass: 'text-warning',
    headerClass: 'border-warning',
    buttons: [
      { text: 'Annulla', action: 'cancel', class: 'btn-secondary', dismiss: true },
      { text: 'Conferma', action: 'confirm', class: 'btn-warning' }
    ],
    backdrop: false,
    ...options
  });
  
  return result.action === 'confirm' && !result.dismissed;
};

/**
 * Shows an alert modal with an info icon and OK button
 * @param message - Alert message to display
 * @param options - Additional modal options
 * @returns Promise that resolves when modal is closed
 */
export const showAlert = async (message: string, options: Partial<ModalOptions> = {}): Promise<ModalResult> => {
  return _showModalInternal({
    title: 'Avviso',
    message,
    icon: 'fas fa-info-circle',
    iconClass: 'text-info',
    headerClass: 'border-info',
    buttons: [
      { text: 'OK', action: 'ok', class: 'btn-info', dismiss: true }
    ],
    ...options
  });
};

/**
 * Shows a success modal with a green checkmark icon
 * @param message - Success message to display
 * @param options - Additional modal options
 * @returns Promise that resolves when modal is closed
 */
export const showSuccess = async (message: string, options: Partial<ModalOptions> = {}): Promise<ModalResult> => {
  return _showModalInternal({
    title: 'Successo',
    message,
    icon: 'fas fa-check-circle',
    iconClass: 'text-success',
    headerClass: 'border-success',
    buttons: [
      { text: 'OK', action: 'ok', class: 'btn-success', dismiss: true }
    ],
    ...options
  });
};

/**
 * Shows a modal prompting for a single text input
 * @param title - Modal title
 * @param label - Input label
 * @param placeholder - Input placeholder text
 * @param options - Additional modal options
 * @returns Promise that resolves with the input value
 */
export const showPrompt = async (title: string, label: string, placeholder: string = '', options: Partial<ModalOptions> = {}): Promise<ModalResult> => {
  const field: ModalField = {
    id: 'promptValue',
    type: 'text',
    label,
    placeholder,
    required: true
  };

  return _showModalInternal({
    title,
    fields: [field],
    buttons: [
      { text: 'Annulla', action: 'cancel', class: 'btn-secondary', dismiss: true },
      { text: 'Conferma', action: 'submit', class: 'btn-primary' }
    ],
    backdrop: false,
    ...options
  });
};

/**
 * Shows a completely custom modal with full control over options
 * @param options - Complete modal configuration
 * @returns Promise that resolves with modal result
 */
export const showCustom = async (options: ModalOptions): Promise<ModalResult> => {
  return _showModalInternal(options);
};

/**
 * Shows a modal with a loading spinner and message
 * @param message - Loading message to display
 * @param options - Additional modal options
 * @returns Modal instance that can be closed programmatically
 */
export const showLoading = async (message: string = 'Caricamento in corso...', options: Partial<ModalOptions> = {}): Promise<{ close: () => void }> => {
  const modalId = options.id || `loading_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  
  const loadingOptions: ModalOptions = {
    id: modalId,
    title: options.title || '',
    message,
    icon: 'fas fa-spinner fa-spin',
    iconClass: 'text-primary',
    showHeader: options.showHeader !== false,
    showFooter: false,
    showCloseButton: false,
    backdrop: false,
    ...options
  };

  // Start showing the modal (don't await it)
  _showModalInternal(loadingOptions);

  // Return an object with a close method
  return {
    close: () => {
      const modalElement = document.getElementById(modalId);
      if (modalElement) {
        const bsModal = window.bootstrap.Modal.getInstance(modalElement);
        if (bsModal) {
          bsModal.hide();
        }
      }
    }
  };
};